import React from 'react';
import { Lock, CheckCircle, AlertCircle, Sparkles, RefreshCw } from 'lucide-react';
import { LotteryBallGroup } from '../LotteryBall';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const COLOR_CLASSES = {
  green: { selected: 'border-green-500 bg-green-900/30', default: 'border-slate-700 hover:border-green-500/50', text: 'text-green-400' },
  blue: { selected: 'border-blue-500 bg-blue-900/30', default: 'border-slate-700 hover:border-blue-500/50', text: 'text-blue-400' },
  pink: { selected: 'border-pink-500 bg-pink-900/30', default: 'border-slate-700 hover:border-pink-500/50', text: 'text-pink-400' },
};

export const StrategySelector = ({ strategies, selectedStrategy, setSelectedStrategy, setRecommendation, isStrategyAvailable }) => (
  <Card className="glass-card lg:col-span-2" data-testid="strategy-selection-card">
    <CardHeader>
      <CardTitle className="text-white text-lg">Estrategia</CardTitle>
      <CardDescription className="text-slate-400">Elige el criterio de análisis del historial</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {strategies.map((strategy) => {
          const Icon = strategy.icon;
          const isSelected = selectedStrategy === strategy.id;
          const isAvailable = isStrategyAvailable(strategy.id);
          const cc = COLOR_CLASSES[strategy.color] || COLOR_CLASSES.green;
          return (
            <button
              key={strategy.id}
              onClick={() => {
                if (isAvailable) { setSelectedStrategy(strategy.id); setRecommendation(null); }
                else toast.error(`Estrategia ${strategy.name} requiere plan ${strategy.minPlan === 'premium' ? 'Premium' : 'VIP'}`);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                isAvailable ? (isSelected ? cc.selected : cc.default) : 'border-slate-800 bg-slate-900/50 opacity-60 cursor-not-allowed'
              }`}
              data-testid={`strategy-${strategy.id}`}
              disabled={!isAvailable}
            >
              {!isAvailable && <div className="absolute top-2 right-2"><Lock size={14} className="text-slate-500" /></div>}
              <Icon size={24} className={`mb-2 ${cc.text}`} />
              <p className="text-white font-bold mb-1">{strategy.name}</p>
              <p className="text-slate-400 text-xs mb-2">{strategy.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-xs">Selección estadística</span>
                {!isAvailable && <Badge className="text-xs bg-slate-700 text-slate-400">{strategy.minPlan === 'premium' ? 'Premium' : 'VIP'}</Badge>}
              </div>
            </button>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

export const GenerateButton = ({ loading, hasSubscription, canGenerateMore, onClick, disabled }) => (
  <div className="flex justify-center mb-8">
    <Button onClick={onClick} disabled={disabled} className="btn-primary text-lg px-10 py-6" data-testid="generate-recommendation-btn">
      {loading && <span className="flex items-center gap-2"><RefreshCw size={20} className="animate-spin" />Generando...</span>}
      {!loading && !hasSubscription && <span className="flex items-center gap-2"><Lock size={20} />Suscripcion Requerida</span>}
      {!loading && hasSubscription && !canGenerateMore && <span className="flex items-center gap-2"><AlertCircle size={20} />Limite Alcanzado</span>}
      {!loading && hasSubscription && canGenerateMore && <span className="flex items-center gap-2"><Sparkles size={20} />Generar Recomendacion</span>}
    </Button>
  </div>
);

export const RecommendationResult = ({ recommendation, strategies, lotteryName }) => (
  <Card className="glass-card border-green-500/30 animate-fade-in" data-testid="recommendation-result">
    <CardHeader className="text-center">
      <div className="flex items-center justify-center gap-2 mb-2">
        <CheckCircle size={24} className="text-green-400" />
        <CardTitle className="text-white text-xl">Numeros Recomendados!</CardTitle>
      </div>
      <CardDescription className="text-slate-400">
        {lotteryName} - Estrategia {strategies.find(s => s.id === recommendation.strategy)?.name}
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="flex justify-center py-6">
        <LotteryBallGroup numbers={recommendation.numbers} variant="recommended" size="xl" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 bg-slate-800/50 rounded-lg">
          <p className="text-slate-400 text-sm mb-1">Historial utilizado</p>
          <p className="text-white text-sm">{recommendation.reasoning?.sample_size ?? '—'} sorteos recientes</p>
          <p className="text-slate-400 text-xs">Último resultado: {recommendation.reasoning?.last_result_date || 'No disponible'}</p>
        </div>
        <div className="p-4 bg-slate-800/50 rounded-lg">
          <p className="text-slate-400 text-sm mb-1">Factores Considerados</p>
          <ul className="text-white text-sm space-y-1">
            {recommendation.reasoning?.factors?.slice(0, 3).map((factor, idx) => (
              <li key={`factor-${idx}`} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />{factor}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {recommendation.reasoning?.evaluation && (
        <div className="p-4 bg-slate-800/50 rounded-lg space-y-3" data-testid="recommendation-evaluation">
          <p className="text-white font-medium">Evaluación con sorteos posteriores</p>
          <p className="text-slate-400 text-sm">
            Cada selección se reconstruye usando solo los resultados anteriores al sorteo evaluado.
          </p>
          {recommendation.reasoning.evaluation.tested_draws > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div><p className="text-slate-400">Casos evaluados</p><p className="text-white">{recommendation.reasoning.evaluation.tested_draws}</p></div>
                <div><p className="text-slate-400">Con alguna coincidencia</p><p className="text-white">{recommendation.reasoning.evaluation.hit_draws} / {recommendation.reasoning.evaluation.tested_draws} ({recommendation.reasoning.evaluation.hit_rate_pct}%)</p></div>
                <div><p className="text-slate-400">Referencia aleatoria calculada</p><p className="text-white">{recommendation.reasoning.evaluation.random_baseline_pct}%</p></div>
              </div>
              <p className="text-slate-400 text-xs">Periodo: {recommendation.reasoning.evaluation.first_test_date} — {recommendation.reasoning.evaluation.last_test_date}. Coincidencia de al menos un número sin importar posición; no mide premios ni rentabilidad.</p>
            </>
          ) : <p className="text-slate-300 text-sm">Todavía no hay suficientes sorteos posteriores para evaluar esta estrategia.</p>}
          <p className="text-amber-200 text-sm">
            {recommendation.reasoning.evaluation.status === 'limited_history' ? 'Muestra de evaluación limitada. ' : ''}
            Esta comparación histórica no demuestra una ventaja para el próximo sorteo.
          </p>
        </div>
      )}
      <div className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
        <AlertCircle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-amber-200 text-sm">
          Recuerda: Las recomendaciones se basan en analisis estadistico historico y <strong>no garantizan resultados</strong>. Cada sorteo es un evento independiente. Juega responsablemente.
        </p>
      </div>
    </CardContent>
  </Card>
);
